// Interactive Insights: step crash-by-crash through every CORRELATED crash
// (a crash with >=1 RDV-identified deficiency at its location, under the
// current filters), with a plain-language narrative per crash and an impact
// summary (potentially avoidable / severity-reducible crashes + FHWA
// comprehensive-cost figure). Navigating flies the map to the crash and opens
// its popup. Sort by milepost or by "worst offenders" (crash cost, findings).
import React, { useEffect, useMemo, useState } from 'react';
import {
  computeImpactSummary, crashSentence, findingSentence, fmtUsd,
  lrsLine, visibleFindings,
} from './insightsText';
import { INSIGHT_SORTS, sortCrashesForInsights } from './safetyFilters';

// "Go to Report": one relevant report -> direct button; several -> chooser.
// URLs are the analysis docs' own UrlReport deep links (they anchor the online
// report at the deficiency's mile point), or the derived hcurves /
// guardrail_warrant dashboard pages.
export function GoToReport({ findings }) {
  const [pick, setPick] = useState('');
  const reports = findings
    .filter(([, f]) => f.reportUrl)
    .map(([a, f]) => [a, f.reportUrl]);
  if (!reports.length) return null;
  if (reports.length === 1) {
    return (
      <a className="safety-btn safety-report-btn" href={reports[0][1]}
        target="_blank" rel="noreferrer">
        Go to {reports[0][0]} Report ↗
      </a>
    );
  }
  return (
    <span className="safety-report-choose">
      <select value={pick} onChange={(e) => setPick(e.target.value)}>
        <option value="">Choose report…</option>
        {reports.map(([a, url]) => <option key={a} value={url}>{a} report</option>)}
      </select>
      <a className={`safety-btn safety-report-btn ${pick ? '' : 'disabled'}`}
        href={pick || undefined} target="_blank" rel="noreferrer"
        onClick={(e) => { if (!pick) e.preventDefault(); }}>
        Go to Report ↗
      </a>
    </span>
  );
}

export default function InsightsPanel({ crashes, road, analysis, onFocus, costTable, program, policyLabel, stats, crashNoun = 'crash',
  defaultSort = 'mp', weights = null, sortTitle = null }) {
  // weights / sortTitle: the Safety Overview's worst-offender weights and their explanation
  const [sortBy, setSortBy] = useState(defaultSort);
  const correlated = useMemo(
    () => sortCrashesForInsights(
      (crashes || []).filter((c) => visibleFindings(c, analysis).length > 0),
      sortBy, costTable?.values || {}, weights),
    [crashes, analysis, sortBy, costTable, weights]
  );
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [analysis, correlated.length, sortBy]);

  const summary = useMemo(() => computeImpactSummary(correlated, costTable), [correlated, costTable]);

  if (!correlated.length) {
    return (
      <div className="safety-muted">
        No correlated crashes in the current selection — no crash has a
        {analysis === 'ALL' ? 'n RDV-identified' : ` ${analysis}`} deficiency at its location.
      </div>
    );
  }

  const i = Math.min(idx, correlated.length - 1);
  const crash = correlated[i];
  const findings = visibleFindings(crash, analysis);
  // Prev / Next point the map at the crash; "Show on map" also brings the map into view
  const focusCrash = (c, scroll = true) => onFocus && onFocus({
    lat: c.lat, lng: c.lon, zoom: 15, crashId: c.id, key: `insight-${c.id}-${Date.now()}`,
  }, { scroll });

  const go = (next) => {
    const j = (i + next + correlated.length) % correlated.length;
    setIdx(j);
    focusCrash(correlated[j], false);
  };

  return (
    <div className="safety-insights">
      <div className="safety-insights-nav">
        <button type="button" className="safety-btn" onClick={() => go(-1)}>‹ Prev</button>
        <span className="safety-insights-counter">
          Correlation {i + 1} of {correlated.length}
        </span>
        <button type="button" className="safety-btn" onClick={() => go(1)}>Next ›</button>
        <button type="button" className="safety-btn safety-btn-ghost" onClick={() => focusCrash(crash)}>
          Show on map
        </button>
        <label className="safety-filter safety-filter-inline" title={sortBy === 'worst' && sortTitle ? sortTitle : 'Milepost order, or the costliest crashes (KABCO cost, then number of findings) first'}>
          <span>Sort</span>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            {INSIGHT_SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
      </div>

      {stats && (
        <div className="safety-info-chips" title="Mechanism counters over the filtered crashes">
          {stats.nGlareMatched > 0 && <span className="safety-info-chip"><b>{stats.nGlareMatched}</b> glare-time matches</span>}
          {stats.nWetSurface > 0 && <span className="safety-info-chip"><b>{stats.nWetSurface}</b> wet / snow surface</span>}
          {stats.nEmbankment > 0 && <span className="safety-info-chip"><b>{stats.nEmbankment}</b> embankment / overturn</span>}
          {stats.nPassing > 0 && <span className="safety-info-chip"><b>{stats.nPassing}</b> head-on / opposite-direction</span>}
          {stats.nAnimal > 0 && <span className="safety-info-chip"><b>{stats.nAnimal}</b> animal strikes</span>}
        </div>
      )}
      <div className="safety-insight-card">
        <div className="safety-insight-lrs">{lrsLine(crash, crash.road || road)}
          <span className="safety-insight-date">
            {crash.datetime ? ` · ${crash.datetime.slice(0, 16).replace('T', ' ')}` : ''}
          </span>
        </div>
        <p className="safety-insight-crash">{crashSentence(crash)}</p>
        <ul className="safety-insight-findings">
          {findings.map(([a, f]) => (
            <li key={a}><strong>{a}</strong> — {findingSentence(a, f, crash)}</li>
          ))}
        </ul>
        {crash.isAnimal && (
          <p className="safety-muted">Animal strike — the sight-distance / sun-glare
            deficiency is a case-dependent contributor (later detection of the animal).</p>
        )}
        <div className="safety-insight-actions">
          <GoToReport findings={findings} />
        </div>
      </div>

      <div className="safety-insight-summary">
        <p>
          <strong className="safety-key">{summary.n} {crashNoun}{summary.n === 1 ? '' : 'es'}</strong> in this
          selection occurred at a location with at least one RDV-identified deficiency
          ({Object.entries(summary.byKabco).map(([k, n]) => `${n} ${k}`).join(', ')} by
          KABCO maximum injury). Had the corresponding RDV analysis recommendations been
          implemented, these crashes are candidates for <em>prevention or reduced injury
          severity</em>.
        </p>
        {summary.mitigated > 0 && (
          <p>
            In <strong>{summary.mitigated}</strong> of them, a guardrail already in place at an
            RDV-warranted location was struck — likely reducing severity. These crashes
            <em> validate</em> the GRW warrant rather than argue for a new installation.
          </p>
        )}
        <p>
          Estimated comprehensive societal <strong className="safety-key">cost of these crashes: {fmtUsd(summary.cost)}</strong>
          {summary.lives > 0 && (
            <> — including <strong className="safety-key">{summary.lives} li{summary.lives === 1 ? 'fe' : 'ves'} that
              might have been saved</strong></>
          )}.
        </p>
        {summary.animal > 0 && (
          <p className="safety-muted">
            {summary.animal} of them {summary.animal === 1 ? 'is an animal strike' : 'are animal strikes'} at a
            sight-distance or sun-glare deficiency — counted above; use the "Exclude animal strikes" filter
            to leave them out.
          </p>
        )}
        {program && program.totals.sites > 0 && (
          <p className="safety-insight-cmf">
            <strong>Expected value of acting.</strong> Applying FHWA CMF Clearinghouse crash
            modification factors ({policyLabel}) to the crashes that match each countermeasure's
            scope, the <strong>{program.totals.counted}</strong> recommended
            site{program.totals.counted === 1 ? '' : 's'} in this selection are expected to
            prevent <strong className="safety-key">{program.totals.preventedPerYr} crashes per year</strong>, worth&nbsp;
            <strong className="safety-key">{fmtUsd(program.totals.benefitPerYr)} per year</strong> at {summary.source}
            {program.totals.bcRatio != null && (
              <> — a program benefit-cost ratio of <strong>{program.totals.bcRatio}</strong> at the
                placeholder unit costs</>
            )}. See the Countermeasure program section for the site-by-site list. Sun-glare (GLR)
            sites are contributing factors without a benefit figure: no published CMF exists for
            sun glare.
          </p>
        )}
        <p className="safety-muted safety-insight-source">
          Cost basis: {summary.source}. Correlation is spatial/mechanistic coincidence with a
          deficiency, not established causation; treat as candidates for engineering review.
          {program && ' CMF estimates are expected values under the cited studies’ conditions; star rating and standard error are shown per site.'}
        </p>
      </div>
    </div>
  );
}
